import React from 'react';

/**
 * One honest line about what is wrong, or what is simply not there yet.
 * `tone="quiet"` for "nothing has happened", loud for something broken now.
 */
export const Notice: React.FC<{
  children: React.ReactNode;
  tone?: 'quiet' | 'error';
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ children, tone = 'error', icon, action, className = '' }) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-3.5 py-2.5 text-[13px] ${
      tone === 'error' ? 'bg-primary-50 text-primary-700' : 'bg-neutral-50 text-neutral-600'
    } ${className}`}
  >
    {icon}
    <span className="min-w-0 flex-1">{children}</span>
    {action}
  </div>
);

/** A dot-separated meta line that never leaves a dangling separator */
export const MetaLine: React.FC<{ parts: Array<React.ReactNode>; className?: string }> = ({ parts, className = '' }) => {
  const shown = parts.filter((part) => part !== null && part !== undefined && part !== '' && part !== false);
  if (shown.length === 0) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-neutral-400 ${className}`}>
      {shown.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true">·</span>}
          <span className="min-w-0">{part}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

export type BarTone = 'neutral' | 'warn' | 'stop';

const BAR_FILL: Record<BarTone, string> = {
  neutral: 'bg-neutral-400',
  warn: 'bg-amber-500',
  stop: 'bg-primary-600',
};

/** Thin share bar. Decorative — the number beside it always states the value. */
export const ShareBar: React.FC<{ value: number; tone?: BarTone; className?: string }> = ({
  value,
  tone = 'neutral',
  className = '',
}) => {
  const width = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100;
  return (
    <span aria-hidden="true" className={`block h-1 w-full overflow-hidden rounded-full bg-neutral-100 ${className}`}>
      <span className={`block h-full rounded-full ${BAR_FILL[tone]}`} style={{ width: `${width}%` }} />
    </span>
  );
};
